import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { Trash2, Plus, Percent, Hash, ToggleLeft, ToggleRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PromoCodesManager = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCode, setNewCode] = useState({
    code: '',
    discount_percent: 10,
    max_uses: 0
  });

  const fetchPromoCodes = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/promo-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromoCodes(response.data);
    } catch (error) {
      console.error('Error fetching promo codes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const handleAddCode = async () => {
    if (!newCode.code.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/promo-codes`, {
        code: newCode.code.toUpperCase(),
        discount_percent: parseFloat(newCode.discount_percent) || 0,
        max_uses: parseInt(newCode.max_uses) || 0,
        current_uses: 0,
        active: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Promo code added successfully');
      setNewCode({ code: '', discount_percent: 10, max_uses: 0 });
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to add promo code');
    }
  };

  const handleToggleActive = async (code, currentActive) => {
    try {
      const token = localStorage.getItem('admin_token');
      const promoCode = promoCodes.find(p => p.code === code);
      await axios.post(`${API}/admin/promo-codes`, {
        ...promoCode,
        active: !currentActive
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Promo code ${!currentActive ? 'activated' : 'deactivated'}`);
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to update promo code');
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/promo-codes/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Promo code deleted');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Percent className="w-5 h-5" />
          Promo Codes / Promotivni Kodovi
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add new promo code */}
        <div className="bg-stone-50 p-4 rounded-lg space-y-4">
          <h4 className="font-medium">Add New Promo Code</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Code</Label>
              <Input
                value={newCode.code}
                onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                placeholder="e.g., SUMMER20"
              />
            </div>
            <div>
              <Label>Discount %</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={newCode.discount_percent}
                onChange={(e) => setNewCode({ ...newCode, discount_percent: e.target.value })}
                placeholder="10"
              />
            </div>
            <div>
              <Label>Max Uses (0 = unlimited)</Label>
              <Input
                type="number"
                min="0"
                value={newCode.max_uses}
                onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })}
                placeholder="0"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleAddCode} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Code
              </Button>
            </div>
          </div>
        </div>

        {/* List of promo codes */}
        <div className="space-y-3">
          <h4 className="font-medium">Active Promo Codes</h4>
          {promoCodes.length === 0 ? (
            <p className="text-stone-500 text-sm">No promo codes created yet.</p>
          ) : (
            <div className="space-y-2">
              {promoCodes.map((promo) => (
                <div
                  key={promo.code}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    promo.active ? 'bg-white border-stone-200' : 'bg-stone-100 border-stone-300'
                  }`}
                >
                  <div className="flex items-center gap-6">
                    <div>
                      <span className="font-mono font-bold text-lg">{promo.code}</span>
                      {!promo.active && (
                        <span className="ml-2 text-xs bg-stone-300 px-2 py-0.5 rounded">INACTIVE</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-green-600">
                      <Percent className="w-4 h-4" />
                      <span className="font-semibold">{promo.discount_percent}%</span>
                    </div>
                    <div className="flex items-center gap-1 text-stone-600 text-sm">
                      <Hash className="w-4 h-4" />
                      <span>
                        {promo.current_uses} / {promo.max_uses === 0 ? '∞' : promo.max_uses} uses
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleActive(promo.code, promo.active)}
                      title={promo.active ? 'Deactivate' : 'Activate'}
                    >
                      {promo.active ? (
                        <ToggleRight className="w-5 h-5 text-green-600" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-stone-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(promo.code)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PromoCodesManager;
